/**
 * Evacuation Capacity Calculator v1.7
 * PSP/CRE Tool v3.0
 * v1.8 (March 17, 2026): BUG-141 — AD threat label and modifier mismatch Tab3 vs Tab5.
 *   Root cause: getTacticalEvacSummary() mapped Tab3 ad_threat code (LOW/MEDIUM/HIGH)
 *   through ENVIRONMENT_MODIFIERS (PERMISSIVE/CONTESTED/DENIED). This produced:
 *   - Label: AD_LABELS.CONTESTED = 'Medium (MANPADS)' — wrong; Tab3 shows 'Mobile SAM'
 *   - Modifier: ENVIRONMENT_MODIFIERS.CONTESTED.air_availability_modifier = 0.50 (50%)
 *     — wrong; Tab3 shows 60% (AD_THREAT_MODIFIERS.MEDIUM.sortie_modifier)
 *   Fix: import AD_THREAT_MODIFIERS from helicopterSpecs.js and use it directly.
 *   When Tab3 ad_threat code is present, read label from .description and modifier from
 *   .sortie_modifier. ENVIRONMENT_MODIFIERS path retained as fallback for Tab2 threat_level.
 *
 * v1.7 (March 7, 2026): ENV-002 + ENV-003 — Fix getTacticalEvacSummary() data paths.
 *   ENV-002: Function was reading from three wrong paths — none populated by Tab3 v4.8+:
 *     OLD: medConfig.ambulance_config         → CORRECT: tactical_evac_config.fleet
 *     OLD: medConfig.medevac_config            → CORRECT: tactical_evac_config.air_pool
 *     OLD: scenario.environment.threat_level  → CORRECT: tactical_evac_config.ad_threat
 *     OLD: scenario.environment.weather       → CORRECT: tactical_evac_config.weather
 *   Result: always 0 vehicles / 0 sorties / PERMISSIVE regardless of Tab3 configuration.
 *   ENV-003: scenario.environment is NOT saved by Tab2. Tab2 saves to:
 *     operation_parameters.threat_level (keys: 'MODERATE_THREAT', 'LOW_THREAT', etc.)
 *     operation_parameters.environmental_factors.threat_level (same)
 *   Added TAB2_THREAT_TO_ENV mapping for fallback when Tab3 ad_threat absent.
 *   Added dual weather-key lookup (Tab3 VMC_CLEAR style + Tab2 CLEAR style).
 *   hasGround/hasAir guards updated to use fleet/air_pool presence checks.
 *   Nominal sorties now read directly from air_pool.nominal_daily_sorties (Tab3-configured).
 *
 * v1.6 (March 7, 2026): AIR-CAP-001 — fix double-application of availMod in calculateAirCapacity().
 *   BUG: availMod was applied twice:
 *     (1) flightHoursAvailable = AIR_DAY_HOURS × availMod  ← correct: fewer sortie hours
 *     (2) allocatedAvailable = Math.floor(allocated × availMod)  ← WRONG: kills aircraft
 *   Math.floor(1 × 0.38) = 0 with contested AD + maintenance + crew → air_cap_per_day = 0
 *   for any configuration where nominal_daily_sorties ≤ 6 (helicopterCount = 1).
 *   FIX: Remove availMod from aircraft count lines. Aircraft physically exist; availability
 *   degrades sortie rate via flightHoursAvailable — applying it to count is double-counting.
 *   on_call 0.5 factor retained (readiness, not availability). No availMod on on_call either.
 *   Static planning model: aircraft count constant across all operation days.
 *   Day-by-day attrition = Tab 6 / ENH-002 scope — not modelled in Tabs 1–5.
 *
 * v1.5 (March 7, 2026): TAC-EVAC-001 root cause fix in calculateGroundCapacity().
 *   BUG: capacity initialisation used field name 'patients' (stub) on each vehicle-type object.
 *   The for-loop replaces the entire object for any type with count > 0, setting patients_per_day.
 *   When ALL vehicle counts are 0 (no fleet configured), every iteration hits `continue`
 *   and the stub objects are never replaced — patients_per_day is never defined on any of them.
 *   totalPatients = undefined + undefined + undefined = NaN.
 *   NaN propagated to computeTacticalEvacSufficiency() in casualtyCalculator.js:
 *     groundCap.total_patients_per_day = NaN
 *     NaN ?? 0 = NaN  (nullish coalescing does NOT catch NaN)
 *     sufficiencyRatio = NaN / hourlyDemand = NaN
 *     NaN < 0.50 / 0.75 / 1.00 → all false (NaN comparisons always false)
 *     status stays 'GREEN' — misleading: fleet=0, demand=222 displayed GREEN (0%)
 *   FIX: Initialise with patients_per_day: 0. Zero-fleet scenario now correctly returns
 *   total_patients_per_day: 0 → sufficiencyRatio = 0 → status = RED (< 0.50).
 *
 * v1.4 (March 6, 2026): Phase 5.1 — getTacticalEvacSummary().
 *   NEW EXPORT: getTacticalEvacSummary(scenario) — builds tactical evac summary for Tab5 card.
 *   Reads scenario.medical_configuration ambulance_config / medevac_config and
 *   environment.threat_level / weather to derive:
 *     ad_label / ad_modifier — from ENVIRONMENT_MODIFIERS (threat → PERMISSIVE/CONTESTED/DENIED)
 *     weather_label / weather_modifier — from WEATHER_MODIFIERS
 *     combined_pct — air_availability as percentage for Tab5 colour coding
 *     fleet { total_vehicles, available_vehicles, utilization_pct }
 *     air { nominal_daily_sorties, effective_daily_sorties, degradation_pct, pool_label }
 *     warnings[] — generated when degradation >30% or fleet <50% available
 *   Returns null when no ambulance or medevac config present (card hides itself).
 * v1.3 FIX: calculateAirCapacity integrates MEDEVAC_OPERATIONAL_FACTORS and TERRAIN_MODIFIERS.
 * v1.2 (February 25, 2026): ESLint — removed unused MEDEVAC_OPERATIONAL_FACTORS and
 *   TERRAIN_MODIFIERS imports. Both were imported from helicopterSpecs.js for anticipated
 *   future use (documented in comment) but never referenced in file body.
 *   DEFAULT_HELICOPTER_SPECS (local const) is the operative helicopter constant.
 * v1.1 FIX (February 25, 2026): Corrected import paths (../constants/ → ../../constants/);
 *   replaced non-existent getAmbulanceSpec with AMBULANCE_TYPES lookup;
 *   replaced non-existent getHelicopterSpecs with MEDEVAC_OPERATIONAL_FACTORS/TERRAIN_MODIFIERS;
 *   paths reverted to ../constants/ (file is in src/calculationEngine/, not src/calculationEngine/modules/).
 * 
 * PURPOSE:
 * Calculates evacuation capacity using per-facility assets from ForceBuilder v2.5.0
 * Integrates with casualtyCalculator v4.0 for patient flow modeling
 * 
 * KEY FEATURES:
 * - Per-facility asset tracking (EA-001)
 * - Inter-facility distance calculations from FEBA positions
 * - Evacuation chain flow modeling (Role 1→2B→2E→3)
 * - Capacity saturation detection
 * - PULL/PUSH method support (EA-003)
 * - Environment modifiers (PERMISSIVE/CONTESTED/DENIED)
 * 
 * ARCHITECTURE:
 * ForceBuilder creates facilities with organic_assets → 
 * This calculator computes capacity →
 * casualtyCalculator uses capacity for mortality/DOW →
 * Tab 5 reports show utilization
 * 
 * @version 1.0.0
 * @author Dr. Tatar Radu MD
 * @institution Romanian Military Medical Institute
 * @date December 27, 2024
 */

// ============================================================================
// IMPORTS
// ============================================================================

// Import ambulance types for ground evacuation
// helicopterSpecs.js — MEDEVAC_OPERATIONAL_FACTORS and TERRAIN_MODIFIERS
// Used in calculateAirCapacity():
//   MEDEVAC_OPERATIONAL_FACTORS: maintenance_availability, crew_availability,
//     sorties_per_aircraft_per_day (doctrinal sortie ceiling by environment),
//     patients_per_sortie.mixed_load (replaces hardcoded 0.8/0.2 capacity mix)
//   TERRAIN_MODIFIERS: sortie_modifier (LZ availability degradation by terrain type)
import { groundGroupCapacity, groundBaseSpeedKph } from './modules/evacuationCapacityModel.js';
import { TACTICAL_CLASS_REFERENCE_VEHICLE, GROUND_HANDLING_MIN, HELICOPTER_TURNAROUND } from '../constants/evacuationCapacityParameters.js';
import {
  MEDEVAC_OPERATIONAL_FACTORS,
  TERRAIN_MODIFIERS,
  AD_THREAT_MODIFIERS,
  getHelicopterSpecs
} from '../constants/helicopterSpecs.js';

// v1.1 FIX: Replaced non-existent getAmbulanceSpec with AMBULANCE_TYPES (canonical export, ambulanceTypes.js v2.2)
// Path ../constants/ is correct for src/calculationEngine/
import {
  AMBULANCE_TYPES,
  getAmbulanceCalcSpec
} from '../constants/ambulanceTypes.js';

// CALC-005: evacuation paradigm comes from Tab 2 via one resolver (evacuationParadigms.js)
import { resolveEvacuationParadigm } from '../constants/evacuationParadigms.js';

// CALC-011: planner's terrain/weather (Tab 2) via one resolver
import { resolveScenarioEnvironment } from './scenarioEnvironment.js';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Environment modifiers affecting evacuation capacity
 */
const ENVIRONMENT_MODIFIERS = {
  PERMISSIVE: {
    ground_speed_modifier: 1.0,
    air_availability_modifier: 1.0,
    route_modifier: 1.0,
    description: 'Friendly control, minimal threat'
  },
  CONTESTED: {
    ground_speed_modifier: 0.6,      // Slower convoy movement
    air_availability_modifier: 0.5,   // 50% helicopter availability
    route_modifier: 1.3,              // Longer routes required
    description: 'Active enemy presence, AD threat'
  },
  DENIED: {
    ground_speed_modifier: 0.4,       // Very slow, tactical movement
    air_availability_modifier: 0.2,   // Minimal helicopter ops
    route_modifier: 1.8,              // Significant detours
    description: 'Enemy control, high threat'
  }
};

/**
 * Time of day modifiers
 */
const TIME_OF_DAY_MODIFIERS = {
  DAY: {
    ground_speed_modifier: 1.0,
    air_availability_modifier: 1.0
  },
  NIGHT: {
    ground_speed_modifier: 0.7,       // NVG operations slower
    air_availability_modifier: 0.8    // Some crews not NVG qualified
  },
  LICO: {  // Limited visibility conditions
    ground_speed_modifier: 0.5,
    air_availability_modifier: 0.3
  }
};

/**
 * Weather modifiers
 */
const WEATHER_MODIFIERS = {
  CLEAR: { ground: 1.0, air: 1.0 },
  LIGHT_RAIN: { ground: 0.9, air: 0.9 },
  HEAVY_RAIN: { ground: 0.7, air: 0.5 },
  SNOW: { ground: 0.6, air: 0.4 },
  FOG: { ground: 0.5, air: 0.2 },
  STORM: { ground: 0.3, air: 0.0 }
};

/**
 * Operational hours per day
 */
const OPERATIONAL_HOURS = {
  GROUND: 16,         // 16 hours ground ops
  AIR_DAY: 12,        // 12 hours day flight
  AIR_24H: 20,        // 20 hours with NVG ops
  SUSTAINED: 18       // Sustained ops (with rest rotations)
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate distance between two facilities based on FEBA positions
 * 
 * @param {Object} fromFacility - Source facility with position_feba_km
 * @param {Object} toFacility - Destination facility with position_feba_km
 * @param {string} environment - PERMISSIVE/CONTESTED/DENIED
 * @returns {number} Distance in km
 */
function calculateInterFacilityDistance(fromFacility, toFacility, environment = 'PERMISSIVE') {
  const fromPos = fromFacility.position_feba_km || fromFacility.position?.feba_km || 5;
  const toPos = toFacility.position_feba_km || toFacility.position?.feba_km || 20;
  
  // Base distance is difference in FEBA positions
  let distance = Math.abs(toPos - fromPos);
  
  // Apply route modifier for contested environments (routes aren't straight)
  const envMod = ENVIRONMENT_MODIFIERS[environment] || ENVIRONMENT_MODIFIERS.PERMISSIVE;
  distance = distance * envMod.route_modifier;
  
  // Minimum practical distance
  return Math.max(distance, 5);
}

/**
 * Calculate effective assets for a facility
 * Effective = Organic + Attached - Detached
 * 
 * @param {Object} facility - Facility with organic/attached/detached assets
 * @returns {Object} Effective asset counts
 */
function calculateEffectiveAssets(facility) {
  const organic = facility.organic_assets || {};
  const attached = facility.attached_assets || {};
  const detached = facility.detached_assets || {};
  
  const effective = {
    ground_ambulances: {
      wheeled_light: Math.max(0, 
        (organic.ground_ambulances?.wheeled_light || 0) +
        (attached.ground_ambulances?.wheeled_light || 0) -
        (detached.ground_ambulances?.wheeled_light || 0)
      ),
      wheeled_armored: Math.max(0,
        (organic.ground_ambulances?.wheeled_armored || 0) +
        (attached.ground_ambulances?.wheeled_armored || 0) -
        (detached.ground_ambulances?.wheeled_armored || 0)
      ),
      tracked: Math.max(0,
        (organic.ground_ambulances?.tracked || 0) +
        (attached.ground_ambulances?.tracked || 0) -
        (detached.ground_ambulances?.tracked || 0)
      )
    },
    helicopters: {
      allocated: Math.max(0,
        (organic.helicopters?.allocated || 0) +
        (attached.helicopters?.allocated || 0) -
        (detached.helicopters?.allocated || 0)
      ),
      on_call: Math.max(0,
        (organic.helicopters?.on_call || 0) +
        (attached.helicopters?.on_call || 0) -
        (detached.helicopters?.on_call || 0)
      )
    }
  };
  
  // Calculate totals
  effective.total_ground = 
    effective.ground_ambulances.wheeled_light +
    effective.ground_ambulances.wheeled_armored +
    effective.ground_ambulances.tracked;
  
  effective.total_air = 
    effective.helicopters.allocated +
    effective.helicopters.on_call;
  
  return effective;
}

/**
 * Spec of one ambulance of a tactical class (wheeled_light, wheeled_armored, tracked), from the constants.
 * The local table of 3 hand-typed ambulances that duplicated ambulanceTypes.js is gone (2026-10-10): each class takes
 * the data of a reference vehicle (TACTICAL_CLASS_REFERENCE_VEHICLE). Throws on an unknown class or vehicle.
 */
function getAmbulanceSpecs(type) {
  const ref = TACTICAL_CLASS_REFERENCE_VEHICLE[type];
  if (!ref || typeof ref !== 'object') {
    throw new Error(`evacuationCapacityCalculator.getAmbulanceSpecs: unknown ambulance class '${type}' (valid: wheeled_light, wheeled_armored, tracked)`);
  }
  const vehicle = getAmbulanceCalcSpec(ref.code);
  if (!vehicle) {
    throw new Error(`evacuationCapacityCalculator.getAmbulanceSpecs: reference vehicle '${ref.code}' for class '${type}' is not in AMBULANCE_TYPES (${Object.keys(AMBULANCE_TYPES).join(', ')})`);
  }
  return {
    code: vehicle.code,
    vehicleClass: ref.vehicleClass,
    patient_capacity_litter: vehicle.litter_capacity,
    patient_capacity_ambulatory: vehicle.ambulatory_capacity,
    mixed_litter: vehicle.mixed_litter,
    mixed_ambulatory: vehicle.mixed_ambulatory,
    cruise_speed_kph: vehicle.speed_km_h,
    range_km: vehicle.range_km,
    typical_availability: vehicle.typical_availability,
    // Speed on a paved road: the class speed (Dr. Tatar) capped by the vehicle's cruise speed.
    speed_road_kph: Math.min(groundBaseSpeedKph(ref.vehicleClass, 'PAVED'), vehicle.speed_km_h),
    load_time_minutes: GROUND_HANDLING_MIN.load,
    unload_time_minutes: GROUND_HANDLING_MIN.unload
  };
}

/**
 * Spec of a helicopter type from the constants (helicopterSpecs.js HELICOPTER_TYPES; Tab 3 names such as
 * UH60_BLACKHAWK resolve through HELICOPTER_TYPE_ALIASES). Ground time per sortie = the combined load and unload
 * planning time of MEDEVAC_OPERATIONAL_FACTORS plus the type's refuelling time. The local table of 3 helicopters that
 * duplicated the constants is gone. Throws on an unknown type.
 */
function getHeliSpecs(type = 'UH60_BLACKHAWK') {
  const heli = getHelicopterSpecs(type);
  if (!heli) throw new Error(`evacuationCapacityCalculator.getHeliSpecs: unknown helicopter type '${type}'`);
  return {
    code: heli.code,
    patient_capacity_litter: heli.capacity.stretchers,
    patient_capacity_ambulatory: heli.capacity.ambulatory,
    cruise_speed_kph: heli.performance.cruise_speed_kmh,
    load_time_minutes: MEDEVAC_OPERATIONAL_FACTORS.evac_planning_load_minutes,   // combined load + unload
    unload_time_minutes: 0,
    turnaround_minutes: heli.fuel?.refuel_time_minutes ?? HELICOPTER_TURNAROUND.defaultRefuelMin
  };
}

// ============================================================================
// CORE CAPACITY CALCULATIONS
// ============================================================================

/**
 * Calculate ground evacuation capacity for a facility
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Ground evacuation capacity
 */
function calculateGroundCapacity(config) {
  const {
    facility,
    distanceKm,
    environment = 'PERMISSIVE',
    timeOfDay = 'DAY',
    weather = 'CLEAR',
    terrain = 'MIXED',
    surface = 'PAVED'   // route surface of the leg (Tab 3 B.3: PAVED, GRAVEL, UNPAVED, TRAIL)
  } = config;
  
  const effective = calculateEffectiveAssets(facility);
  const envMod = ENVIRONMENT_MODIFIERS[environment] || ENVIRONMENT_MODIFIERS.PERMISSIVE;
  const todMod = TIME_OF_DAY_MODIFIERS[timeOfDay] || TIME_OF_DAY_MODIFIERS.DAY;
  const wxMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.CLEAR;
  
  // Combined speed modifier
  const speedMod = envMod.ground_speed_modifier * todMod.ground_speed_modifier * wxMod.ground;
  
  // Calculate trips per day for each ambulance type
  // TAC-EVAC-001 FIX: initialise with patients_per_day: 0 (was 'patients: 0' — wrong field name).
  // When all vehicle counts are 0 the loop body never runs; without this the field is undefined
  // on every type → totalPatients = NaN → computeTacticalEvacSufficiency status always GREEN.
  const capacity = {
    wheeled_light: { count: 0, trips_per_day: 0, patients_per_trip: 0, patients_per_day: 0 },
    wheeled_armored: { count: 0, trips_per_day: 0, patients_per_trip: 0, patients_per_day: 0 },
    tracked: { count: 0, trips_per_day: 0, patients_per_trip: 0, patients_per_day: 0 }
  };
  
  // Ground capacity model (2026-10-10, docs/documentation/SPEC_EVAC_CAPACITY.md): speed by vehicle class and route surface, handling
  // time, two 12 h crews per vehicle per day. The old rule used the spec speeds and capped runs at 3 per day whatever
  // the distance (BUG-T40).
  for (const [type, count] of Object.entries(effective.ground_ambulances)) {
    if (count === 0) continue;
    
    const spec = getAmbulanceSpecs(type);
    const cap = groundGroupCapacity({
      vehicles: count,
      availability: spec.typical_availability,
      distanceKm,
      vehicleClass: spec.vehicleClass,
      surface,
      speedModifier: speedMod,
      speedCapKph: spec.cruise_speed_kph,
      rangeKm: spec.range_km,
      litterPlaces: spec.patient_capacity_litter,
      ambulatoryPlaces: spec.patient_capacity_ambulatory,
      mixedLitter: spec.mixed_litter,
      mixedAmbulatory: spec.mixed_ambulatory
    });

    capacity[type] = {
      count: count,
      trips_per_day: cap.runsPerDay,
      patients_per_trip: cap.patientsPerRun,
      patients_per_day: cap.patientsPerDay,
      round_trip_hours: Number.isFinite(cap.cycleMinutes) ? Math.round((cap.cycleMinutes / 60) * 10) / 10 : null
    };
  }
  
  // Total ground capacity
  const totalPatients = 
    capacity.wheeled_light.patients_per_day +
    capacity.wheeled_armored.patients_per_day +
    capacity.tracked.patients_per_day;
  
  return {
    by_type: capacity,
    total_vehicles: effective.total_ground,
    total_patients_per_day: totalPatients,
    modifiers_applied: {
      environment: environment,
      speed_modifier: Math.round(speedMod * 100) / 100,
      distance_km: distanceKm,
      surface,
      // Terrain no longer changes ground speed here: the route's surface, status and weather impact (Tab 3 B.3) already
      // describe the road, and applying a terrain speed factor on top was a double count (ARCH-015). Reported only.
      terrain
    }
  };
}

/**
 * Calculate air evacuation capacity for a facility
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Air evacuation capacity
 */
// v1.3: calculateAirCapacity now fully integrates MEDEVAC_OPERATIONAL_FACTORS and TERRAIN_MODIFIERS:
//   - maintenance_availability × crew_availability applied on top of environment/weather/time modifiers
//   - sorties_per_aircraft_per_day[environment] used as doctrinal ceiling (NATO planning cap)
//   - patients_per_sortie.mixed_load replaces hardcoded 0.8 litter / 0.2 ambulatory mix
//   - terrain parameter added; TERRAIN_MODIFIERS[terrain].sortie_modifier applied to sortie rate
//     (reflects LZ availability degradation: MOUNTAIN/URBAN terrain reduces sortie rate)
function calculateAirCapacity(config) {
  const {
    facility,
    distanceKm,
    environment = 'PERMISSIVE',
    timeOfDay = 'DAY',
    weather = 'CLEAR',
    terrain = 'MIXED',
    helicopterType = 'UH60_BLACKHAWK'
  } = config;
  
  const effective = calculateEffectiveAssets(facility);
  const envMod = ENVIRONMENT_MODIFIERS[environment] || ENVIRONMENT_MODIFIERS.PERMISSIVE;
  const todMod = TIME_OF_DAY_MODIFIERS[timeOfDay] || TIME_OF_DAY_MODIFIERS.DAY;
  const wxMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.CLEAR;

  // --- MEDEVAC_OPERATIONAL_FACTORS: maintenance & crew availability ---
  // These apply regardless of environment — reflect fleet readiness, not threat
  const opFactors = MEDEVAC_OPERATIONAL_FACTORS || {};
  const maintenanceAvail = opFactors.maintenance_availability ?? 0.85;
  const crewAvail        = opFactors.crew_availability        ?? 0.90;

  // Combined availability: environment × weather × time-of-day × maintenance × crew
  const availMod = envMod.air_availability_modifier
                 * todMod.air_availability_modifier
                 * wxMod.air
                 * maintenanceAvail
                 * crewAvail;

  // --- TERRAIN_MODIFIERS: LZ availability affects sortie rate ---
  // Key adapter: Tab 2 terrain -> helicopterSpecs TERRAIN_MODIFIERS keys (FLAT_OPEN, ROLLING, FORESTED,
  // MOUNTAINOUS, URBAN, COMPLEX, MIXED). CALC-011: the old targets (PLAINS, MOUNTAINS, FOREST,
  // SWAMP_WETLAND) do not exist in TERRAIN_MODIFIERS, so every terrain except URBAN fell through to ROLLING.
  // MIXED -> ROLLING keeps the medevacModule convention; DESERT is open ground.
  const terrainKeyMap = {
    MIXED: 'ROLLING', OPEN: 'FLAT_OPEN', DESERT: 'FLAT_OPEN', MOUNTAIN: 'MOUNTAINOUS',
    URBAN: 'URBAN', FOREST: 'FORESTED'
  };
  const terrainKey = terrainKeyMap[terrain?.toUpperCase()] || 'ROLLING';
  const terrainData = TERRAIN_MODIFIERS?.[terrainKey] || TERRAIN_MODIFIERS?.ROLLING || {};
  const terrainSortieMod = terrainData.sortie_modifier ?? 1.0;

  // Get helicopter specs
  const heliSpec = getHeliSpecs(helicopterType);

  // --- Sortie calculation ---
  const flightTimeHours  = (distanceKm * 2) / heliSpec.cruise_speed_kph;
  const groundTimeHours  = (heliSpec.load_time_minutes + heliSpec.unload_time_minutes +
                            heliSpec.turnaround_minutes) / 60;
  const sortieHours      = flightTimeHours + groundTimeHours;

  const flightHoursAvailable = (timeOfDay === 'DAY'
    ? OPERATIONAL_HOURS.AIR_DAY
    : OPERATIONAL_HOURS.AIR_24H) * availMod;

  // Raw sorties from physics, scaled by terrain LZ availability
  const rawSortiesPerAircraft = sortieHours > 0
    ? Math.floor((flightHoursAvailable / sortieHours) * terrainSortieMod)
    : 0;

  // --- MEDEVAC_OPERATIONAL_FACTORS: doctrinal sortie ceiling per environment ---
  // The table keys are upper case (PERMISSIVE, CONTESTED, DENIED). The old lower-case lookup never matched, so the
  // doctrinal sortie ceiling was always Infinity and never applied.
  const envKey = environment.toUpperCase();
  const doctrineSortieMax = opFactors.sorties_per_aircraft_per_day?.[envKey] ?? Infinity;
  const sortiesPerAircraft = Math.min(rawSortiesPerAircraft, doctrineSortieMax);

  // AIR-CAP-001 FIX: availMod must NOT be applied to aircraft count.
  // availMod is already fully applied via flightHoursAvailable (line above) — it reduces
  // the number of sorties each aircraft can fly. Applying it again to the count is
  // double-counting, and with helicopterCount=1 (the common case: 6 sorties ÷ 6/aircraft)
  // Math.floor(1 × availMod) = 0 for any availMod < 1.0, killing all air capacity.
  //
  // Correct model: aircraft physically exist; availability degrades their sortie rate,
  // not whether they are present. This matches the Tab3 architecture where
  // nominal_daily_sorties is the user-entered allocation "before AD/weather degradation"
  // and availMod represents those degradation factors — applied once, via flight hours.
  //
  // on_call factor 0.5 retained: these aircraft are not on immediate standby.
  // No availMod on on_call either — same double-count argument applies.
  //
  // Static planning model: aircraft count is constant across all operation days.
  // Day-by-day attrition (losses, maintenance backlog growth) = Tab 6 / ENH-002 scope.
  const allocatedAvailable = effective.helicopters.allocated;
  const onCallAvailable    = Math.floor(effective.helicopters.on_call * 0.5);
  const totalAvailable     = allocatedAvailable + onCallAvailable;

  // --- MEDEVAC_OPERATIONAL_FACTORS: patients per sortie (mixed load doctrine) ---
  // Planning load per sortie by helicopter type (MEDEVAC_OPERATIONAL_FACTORS.patients_per_sortie[code]). The old lookup
  // read a key ('mixed_load') that the constant does not have, so the constant was never used.
  const patientsPerSortie = opFactors.patients_per_sortie?.[heliSpec.code]
    ?? opFactors.patients_per_sortie?.DEFAULT
    ?? Math.round(heliSpec.patient_capacity_litter * 0.8 + heliSpec.patient_capacity_ambulatory * 0.2);

  const totalPatients = totalAvailable * sortiesPerAircraft * patientsPerSortie;

  return {
    helicopter_type: helicopterType,
    allocated_available: allocatedAvailable,
    on_call_available: onCallAvailable,
    total_available: totalAvailable,
    sorties_per_aircraft: sortiesPerAircraft,
    raw_sorties_before_doctrine_cap: rawSortiesPerAircraft,
    doctrine_sortie_ceiling: doctrineSortieMax,
    patients_per_sortie: patientsPerSortie,
    total_patients_per_day: totalPatients,
    flight_time_minutes: Math.round(flightTimeHours * 60),
    modifiers_applied: {
      environment: environment,
      availability_modifier: Math.round(availMod * 100) / 100,
      maintenance_availability: maintenanceAvail,
      crew_availability: crewAvail,
      terrain_sortie_modifier: terrainSortieMod,
      distance_km: distanceKm
    }
  };
}

/**
 * Calculate total evacuation capacity for a single facility
 * 
 * @param {Object} facility - Facility with Phase 2 assets
 * @param {Object} options - Calculation options
 * @returns {Object} Total evacuation capacity
 */
export function calculateFacilityCapacity(facility, options = {}) {
  const {
    destinationFacility = null,
    distanceKm = null,
    environment = 'PERMISSIVE',
    timeOfDay = 'DAY',
    weather = 'CLEAR',
    terrain = 'MIXED',
    helicopterType = 'UH60_BLACKHAWK'
  } = options;
  
  // Calculate or use provided distance
  let distance = distanceKm;
  if (!distance && destinationFacility) {
    distance = calculateInterFacilityDistance(facility, destinationFacility, environment);
  }
  distance = distance || 20; // Default 20km if not specified
  
  // Calculate ground capacity
  const groundCapacity = calculateGroundCapacity({
    facility,
    distanceKm: distance,
    environment,
    timeOfDay,
    weather,
    terrain
  });
  
  // Calculate air capacity
  const airCapacity = calculateAirCapacity({
    facility,
    distanceKm: distance,
    environment,
    timeOfDay,
    weather,
    terrain,      // v1.3: pass terrain so TERRAIN_MODIFIERS.sortie_modifier applies
    helicopterType
  });
  
  // Combined capacity
  const totalCapacity = groundCapacity.total_patients_per_day + airCapacity.total_patients_per_day;
  
  return {
    facility_id: facility.facility_id || facility.id,
    facility_name: facility.facility_name || facility.name,
    facility_type: facility.role_type || facility.type,
    
    ground: groundCapacity,
    air: airCapacity,
    
    combined: {
      total_patients_per_day: totalCapacity,
      ground_percent: Math.round((groundCapacity.total_patients_per_day / totalCapacity) * 100) || 0,
      air_percent: Math.round((airCapacity.total_patients_per_day / totalCapacity) * 100) || 0
    },
    
    distance_to_destination_km: distance,
    environment: environment
  };
}

// ============================================================================
// EVACUATION CHAIN CAPACITY
// ============================================================================

/**
 * Calculate capacity for entire evacuation chain
 * Models patient flow: Role 1 → Role 2B → Role 2E → Role 3
 * 
 * @param {Object} config - Chain configuration
 * @returns {Object} Chain capacity analysis
 */
export function calculateEvacuationChainCapacity(config) {
  const {
    facilities,          // Array of all facilities with Phase 2 assets
    evacuationChain,     // Chain config from ForceBuilder
    environment = 'PERMISSIVE',
    timeOfDay = 'DAY',
    weather = 'CLEAR',
    terrain = 'MIXED',
    dailyCasualties = 0  // Expected daily casualties for saturation analysis
  } = config;
  
  // Group facilities by role
  const role1s = facilities.filter(f => 
    f.type === 'ROLE1' || f.role_type === 'ROLE_1'
  );
  const role2Bs = facilities.filter(f => 
    f.type === 'ROLE2_BASIC' || f.role_type === 'ROLE_2_BASIC'
  );
  const role2Es = facilities.filter(f => 
    f.type === 'ROLE2_ENHANCED' || f.role_type === 'ROLE_2_ENHANCED'
  );
  const role3s = facilities.filter(f => 
    f.type === 'ROLE3' || f.role_type === 'ROLE_3'
  );
  
  const chainAnalysis = {
    echelons: {
      role1: { count: role1s.length, facilities: [], total_capacity: 0 },
      role2_basic: { count: role2Bs.length, facilities: [], total_capacity: 0 },
      role2_enhanced: { count: role2Es.length, facilities: [], total_capacity: 0 },
      role3: { count: role3s.length, facilities: [], total_capacity: 0 }
    },
    
    chain_links: [],
    bottlenecks: [],
    saturation_analysis: null,
    
    summary: {
      total_ground_capacity: 0,
      total_air_capacity: 0,
      total_combined_capacity: 0,
      chain_configured: evacuationChain?.configured || false
    }
  };
  
  // Calculate Role 1 → Role 2B capacity
  for (const role2b of role2Bs) {
    const supportedRole1s = role1s.filter(r1 => {
      // Check if this Role 2B pulls from this Role 1
      const pullsFrom = role2b.evacuation_config?.picks_up_from || [];
      return pullsFrom.includes(r1.facility_id || r1.id) ||
             // Fallback: same brigade
             r1.hierarchy?.brigade === role2b.hierarchy?.brigade;
    });
    
    let linkCapacity = 0;
    const role1Details = [];
    
    for (const role1 of supportedRole1s) {
      const capacity = calculateFacilityCapacity(role1, {
        destinationFacility: role2b,
        environment,
        timeOfDay,
        weather,
        terrain
      });
      
      linkCapacity += capacity.combined.total_patients_per_day;
      role1Details.push({
        facility_id: role1.facility_id || role1.id,
        capacity: capacity.combined.total_patients_per_day
      });
      
      chainAnalysis.echelons.role1.facilities.push(capacity);
    }
    
    const role2bCapacity = calculateFacilityCapacity(role2b, {
      environment,
      timeOfDay,
      weather,
      terrain
    });
    
    chainAnalysis.chain_links.push({
      from_echelon: 'ROLE1',
      to_echelon: 'ROLE2_BASIC',
      to_facility: role2b.facility_id || role2b.id,
      to_facility_name: role2b.facility_name || role2b.name,
      source_count: supportedRole1s.length,
      combined_capacity: linkCapacity,
      method: role2b.evacuation_config?.incoming_method || 'PULL'
    });
    
    chainAnalysis.echelons.role2_basic.facilities.push(role2bCapacity);
  }
  
  // Calculate Role 2B → Role 2E capacity
  for (const role2e of role2Es) {
    const supportedRole2Bs = role2Bs.filter(r2b => {
      const pullsFrom = role2e.evacuation_config?.picks_up_from || [];
      return pullsFrom.includes(r2b.facility_id || r2b.id) ||
             r2b.hierarchy?.division === role2e.hierarchy?.division;
    });
    
    let linkCapacity = 0;
    
    for (const role2b of supportedRole2Bs) {
      const capacity = calculateFacilityCapacity(role2b, {
        destinationFacility: role2e,
        environment,
        timeOfDay,
        weather,
        terrain
      });
      linkCapacity += capacity.combined.total_patients_per_day;
    }
    
    const role2eCapacity = calculateFacilityCapacity(role2e, {
      environment,
      timeOfDay,
      weather,
      terrain
    });
    
    chainAnalysis.chain_links.push({
      from_echelon: 'ROLE2_BASIC',
      to_echelon: 'ROLE2_ENHANCED',
      to_facility: role2e.facility_id || role2e.id,
      to_facility_name: role2e.facility_name || role2e.name,
      source_count: supportedRole2Bs.length,
      combined_capacity: linkCapacity,
      method: role2e.evacuation_config?.incoming_method || 'PULL'
    });
    
    chainAnalysis.echelons.role2_enhanced.facilities.push(role2eCapacity);
  }
  
  // Calculate Role 2E → Role 3 capacity
  for (const role3 of role3s) {
    const supportedRole2Es = role2Es.filter(r2e => {
      const pullsFrom = role3.evacuation_config?.picks_up_from || [];
      return pullsFrom.includes(r2e.facility_id || r2e.id) ||
             r2e.hierarchy?.corps === role3.hierarchy?.corps;
    });
    
    let linkCapacity = 0;
    
    for (const role2e of supportedRole2Es) {
      const capacity = calculateFacilityCapacity(role2e, {
        destinationFacility: role3,
        environment,
        timeOfDay,
        weather,
        terrain
      });
      linkCapacity += capacity.combined.total_patients_per_day;
    }
    
    const role3Capacity = calculateFacilityCapacity(role3, {
      environment,
      timeOfDay,
      weather,
      terrain
    });
    
    chainAnalysis.chain_links.push({
      from_echelon: 'ROLE2_ENHANCED',
      to_echelon: 'ROLE3',
      to_facility: role3.facility_id || role3.id,
      to_facility_name: role3.facility_name || role3.name,
      source_count: supportedRole2Es.length,
      combined_capacity: linkCapacity,
      method: role3.evacuation_config?.incoming_method || 'PULL'
    });
    
    chainAnalysis.echelons.role3.facilities.push(role3Capacity);
  }
  
  // Calculate totals per echelon
  for (const [echelonName, data] of Object.entries(chainAnalysis.echelons)) {
    data.echelon_name = echelonName; // Add echelon identifier to data
    data.total_capacity = data.facilities.reduce(
      (sum, f) => sum + (f.combined?.total_patients_per_day || 0), 0
    );
    
    chainAnalysis.summary.total_ground_capacity += data.facilities.reduce(
      (sum, f) => sum + (f.ground?.total_patients_per_day || 0), 0
    );
    chainAnalysis.summary.total_air_capacity += data.facilities.reduce(
      (sum, f) => sum + (f.air?.total_patients_per_day || 0), 0
    );
  }
  
  chainAnalysis.summary.total_combined_capacity = 
    chainAnalysis.summary.total_ground_capacity + 
    chainAnalysis.summary.total_air_capacity;
  
  // Identify bottlenecks (links with capacity < expected casualties)
  if (dailyCasualties > 0) {
    for (const link of chainAnalysis.chain_links) {
      if (link.combined_capacity < dailyCasualties) {
        chainAnalysis.bottlenecks.push({
          link: `${link.from_echelon} → ${link.to_facility_name}`,
          capacity: link.combined_capacity,
          demand: dailyCasualties,
          shortfall: dailyCasualties - link.combined_capacity,
          severity: link.combined_capacity < dailyCasualties * 0.5 ? 'CRITICAL' : 'WARNING'
        });
      }
    }
    
    // Saturation analysis
    const minLinkCapacity = Math.min(...chainAnalysis.chain_links.map(l => l.combined_capacity));
    chainAnalysis.saturation_analysis = {
      expected_daily_casualties: dailyCasualties,
      minimum_link_capacity: minLinkCapacity,
      saturation_percent: Math.round((dailyCasualties / minLinkCapacity) * 100),
      is_saturated: dailyCasualties > minLinkCapacity,
      excess_casualties: Math.max(0, dailyCasualties - minLinkCapacity),
      recommendation: dailyCasualties > minLinkCapacity ?
        'Evacuation chain capacity insufficient - consider additional assets or forward staging' :
        dailyCasualties > minLinkCapacity * 0.8 ?
        'Evacuation chain at 80%+ capacity - monitor closely' :
        'Evacuation chain capacity adequate'
    };
  }
  
  return chainAnalysis;
}

// ============================================================================
// GOLDEN HOUR ANALYSIS
// ============================================================================

/**
 * Calculate golden hour achievement probability based on evacuation capacity
 * 
 * @param {Object} config - Configuration
 * @returns {Object} Golden hour analysis
 */
export function calculateGoldenHourAchievement(config) {
  const {
    facility,
    destinationFacility,
    environment = 'PERMISSIVE',
    timeOfDay = 'DAY',
    weather = 'CLEAR'
  } = config;
  
  const distance = calculateInterFacilityDistance(facility, destinationFacility, environment);
  const effective = calculateEffectiveAssets(facility);
  
  const envMod = ENVIRONMENT_MODIFIERS[environment] || ENVIRONMENT_MODIFIERS.PERMISSIVE;
  const todMod = TIME_OF_DAY_MODIFIERS[timeOfDay] || TIME_OF_DAY_MODIFIERS.DAY;
  const wxMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.CLEAR;
  
  // Ground evacuation time (worst case - used if no helicopter)
  const groundSpec = getAmbulanceSpecs('wheeled_armored');
  const groundSpeed = groundSpec.speed_road_kph * envMod.ground_speed_modifier * todMod.ground_speed_modifier * wxMod.ground;
  const groundTimeMinutes = (distance / groundSpeed) * 60 + groundSpec.load_time_minutes;
  
  // Air evacuation time (best case)
  const heliSpec = getHeliSpecs('UH60_BLACKHAWK');
  const airTimeMinutes = (distance / heliSpec.cruise_speed_kph) * 60 + heliSpec.load_time_minutes;
  
  // Blended time based on asset availability (apply time of day modifier to air availability)
  const effectiveAirAvailability = todMod.air_availability_modifier * wxMod.air;
  const airAvailable = effective.total_air > 0 && effectiveAirAvailability > 0.3;
  const blendedTime = airAvailable ?
    (airTimeMinutes * 0.6 + groundTimeMinutes * 0.4) : // 60% get air evac
    groundTimeMinutes;
  
  // Golden hour = 60 minutes POI to Role 2 surgical capability
  // We calculate Role 1 to Role 2 time, add ~15 min for POI to Role 1
  const totalTimeMinutes = blendedTime + 15;
  
  const achievable = totalTimeMinutes <= 60;
  const achievementPercent = achievable ? 100 : Math.round((60 / totalTimeMinutes) * 100);
  
  return {
    distance_km: Math.round(distance),
    ground_time_minutes: Math.round(groundTimeMinutes),
    air_time_minutes: Math.round(airTimeMinutes),
    blended_time_minutes: Math.round(blendedTime),
    total_time_minutes: Math.round(totalTimeMinutes),
    
    golden_hour_achievable: achievable,
    achievement_percent: achievementPercent,
    
    air_evacuation_available: airAvailable,
    limiting_factor: airAvailable ? 
      (achievable ? 'NONE' : 'DISTANCE') :
      'NO_AIR_ASSETS',
    
    recommendations: [
      !airAvailable && 'Allocate helicopter assets for improved golden hour',
      !achievable && distance > 30 && 'Consider forward Role 2 positioning',
      !achievable && wxMod.air < 0.5 && 'Weather limiting air operations'
    ].filter(Boolean)
  };
}

// ============================================================================
// INTEGRATION WITH CASUALTY CALCULATOR
// ============================================================================

/**
 * Prepare evacuation data for casualtyCalculator v4.0
 * Returns data structure compatible with existing medevacAnalysis
 * 
 * @param {Object} scenario - Complete scenario with ForceBuilder data
 * @returns {Object} MEDEVAC analysis for casualty calculator
 */
export function prepareForCasualtyCalculator(scenario) {
  const forceBuilderResult = scenario.force?.forceBuilderResult;
  if (!forceBuilderResult) {
    console.warn('No ForceBuilder data - using legacy evacuation calculation');
    return null;
  }
  
  // Extract facilities from ForceBuilder
  const facilities = [];
  
  // Role 1 facilities
  if (forceBuilderResult.role1_facilities) {
    facilities.push(...forceBuilderResult.role1_facilities.map(f => ({
      ...f,
      type: 'ROLE1'
    })));
  }
  
  // Role 2 Basic
  if (forceBuilderResult.role2_facilities) {
    facilities.push(...forceBuilderResult.role2_facilities.map(f => ({
      ...f,
      type: 'ROLE2_BASIC'
    })));
  }
  
  // Role 2 Enhanced
  if (forceBuilderResult.role2_enhanced_facilities) {
    facilities.push(...forceBuilderResult.role2_enhanced_facilities.map(f => ({
      ...f,
      type: 'ROLE2_ENHANCED'
    })));
  }
  
  // Role 3
  if (forceBuilderResult.role3_facility) {
    facilities.push({
      ...forceBuilderResult.role3_facility,
      type: 'ROLE3'
    });
  }
  
  // CALC-005: environment IS the evacuation paradigm chosen in Tab 2. The previous expression
  // mixed || with a ternary (any set value became CONTESTED) and compared threat_level to
  // 'HIGH'/'EXTREME', which are not threat-level keys (HIGH_THREAT/EXTREME_THREAT).
  const environment = resolveEvacuationParadigm(scenario).paradigm;

  const weather = scenario.environment?.weather || 'CLEAR';
  const terrain = resolveScenarioEnvironment(scenario).terrain;   // CALC-011: planner's terrain (Tab 2)
  const timeOfDay = scenario.operation_parameters?.time_of_day || 'DAY';
  
  // Calculate chain capacity
  const chainAnalysis = calculateEvacuationChainCapacity({
    facilities,
    evacuationChain: forceBuilderResult.evacuation_chain,
    environment,
    weather,
    terrain,
    timeOfDay,
    dailyCasualties: scenario.operation_parameters?.expected_daily_casualties || 0
  });
  
  // Calculate golden hour for representative facility
  let goldenHourAnalysis = null;
  if (facilities.length >= 2) {
    const role1 = facilities.find(f => f.type === 'ROLE1');
    const role2 = facilities.find(f => f.type === 'ROLE2_BASIC' || f.type === 'ROLE2_ENHANCED');
    
    if (role1 && role2) {
      goldenHourAnalysis = calculateGoldenHourAchievement({
        facility: role1,
        destinationFacility: role2,
        environment,
        timeOfDay,
        weather
      });
    }
  }
  
  // Return in format compatible with casualtyCalculator medevacAnalysis
  return {
    version: '2.5.0',
    source: 'evacuationCapacityCalculator',
    
    // Chain analysis
    chain_analysis: chainAnalysis,
    
    // Capacity summary
    capacity: {
      ground_total: chainAnalysis.summary.total_ground_capacity,
      air_total: chainAnalysis.summary.total_air_capacity,
      combined_total: chainAnalysis.summary.total_combined_capacity
    },
    
    // Golden hour
    golden_hour_analysis: goldenHourAnalysis ? {
      blended_gh_achievement_percent: goldenHourAnalysis.achievement_percent,
      air_available: goldenHourAnalysis.air_evacuation_available,
      average_evac_time_minutes: goldenHourAnalysis.total_time_minutes
    } : null,
    
    // Saturation
    saturation: chainAnalysis.saturation_analysis,
    
    // Bottlenecks
    bottlenecks: chainAnalysis.bottlenecks,
    
    // Recommendations
    recommendations: [
      ...(chainAnalysis.saturation_analysis?.recommendation ? 
          [chainAnalysis.saturation_analysis.recommendation] : []),
      ...(goldenHourAnalysis?.recommendations || []),
      ...chainAnalysis.bottlenecks.map(b => 
        `Bottleneck at ${b.link}: ${b.shortfall} patient/day shortfall`
      )
    ],
    
    // Per-facility details for Tab 3 display
    facilities_detail: chainAnalysis.echelons,
    
    // Blue Light recommendation
    blue_light: {
      should_activate: chainAnalysis.saturation_analysis?.is_saturated || 
                      (goldenHourAnalysis && !goldenHourAnalysis.golden_hour_achievable),
      reason: chainAnalysis.saturation_analysis?.is_saturated ?
              'Evacuation chain saturated' :
              'Golden hour not achievable'
    },
    
    // Environment used
    environment_conditions: {
      evacuation_environment: environment,
      weather,
      terrain,
      time_of_day: timeOfDay
    }
  };
}

// ============================================================================
// PHASE 5.1: getTacticalEvacSummary
// Called by Tab5_Reports.jsx renderResultsDashboard() — builds the static card.
// Reads scenario directly (not projections) so no calculator dependency.
// Returns null when ambulance and MEDEVAC config both absent → card hidden.
// ============================================================================

/**
 * Build tactical evacuation summary for Tab5 display card.
 *
 * @param {Object} scenario - Full scenario object
 * @returns {Object|null} Summary shaped for Tab5 destructuring, or null
 */
export function getTacticalEvacSummary(scenario) {
  const medConfig  = scenario?.medical_configuration;

  // ── ENV-002 FIX: Read from canonical tactical_evac_config (AMB-002 path) ─
  // Tab3 saves fleet/air_pool/ad_threat/weather to tactical_evac_config since v4.8.0.
  // Legacy paths (ambulance_config / medevac_config / scenario.environment) no longer
  // populated by Tab3 — reading them always returned empty/undefined → 0 capacity.
  const tecConfig  = medConfig?.tactical_evac_config;
  const fleet      = tecConfig?.fleet      || {};
  const airPool    = tecConfig?.air_pool   || {};

  // Presence check: show card if fleet has any vehicle >0, or air pool has sorties
  const hasGround = Object.values(fleet).some(v =>
    typeof v === 'object' ? ((v.available ?? v.count ?? 0) > 0) : (v > 0)
  );
  const hasAir = (airPool.nominal_daily_sorties ?? 0) > 0;
  if (!hasGround && !hasAir) return null;

  const AD_LABELS = {
    PERMISSIVE: 'None / Low',
    CONTESTED:  'Medium (MANPADS)',
    DENIED:     'High (SA-8/SA-15)'
  };

  // ── ENV-002 FIX: AD threat ────────────────────────────────────────────────
  // BUG-141 FIX: Tab3 saves ad_threat as AD_THREAT_MODIFIERS keys (LOW/MEDIUM/HIGH).
  // Previously mapped through ENVIRONMENT_MODIFIERS (PERMISSIVE/CONTESTED/DENIED) which
  // produced mismatched label and modifier vs Tab3 display:
  //   Tab3 shows: AD_THREAT_MODIFIERS.MEDIUM.description ('Mobile SAM') + sortie_modifier (0.60)
  //   Tab5 showed: AD_LABELS.CONTESTED ('Medium (MANPADS)') + ENVIRONMENT_MODIFIERS.CONTESTED (0.50)
  // Fix: when Tab3 code is present, read label and modifier directly from AD_THREAT_MODIFIERS.
  // ENVIRONMENT_MODIFIERS path retained for Tab2 threat_level fallback and ground modifier.
  // Tab2 saves threat_level as 'MODERATE_THREAT' / 'LOW_THREAT' / 'HIGH_THREAT' etc.
  // scenario.environment is NOT saved by Tab2 — do not read it.
  const TAB2_THREAT_TO_ENV = {
    LOW_THREAT:      'PERMISSIVE', MINIMAL_THREAT: 'PERMISSIVE',
    MODERATE_THREAT: 'CONTESTED',
    HIGH_THREAT:     'DENIED',    EXTREME_THREAT: 'DENIED',
    FULL_COMBAT:     'DENIED',    PEER_THREAT:    'DENIED',
    LOW:      'PERMISSIVE', MINIMAL: 'PERMISSIVE',
    MODERATE: 'CONTESTED',
    HIGH:     'DENIED',    EXTREME: 'DENIED', PEER: 'DENIED', FULL: 'DENIED'
  };
  const TAB3_AD_TO_ENV = {
    LOW:    'PERMISSIVE',
    MEDIUM: 'CONTESTED',
    HIGH:   'DENIED'
  };
  const rawTab3AD = (tecConfig?.ad_threat || '').toUpperCase();
  const rawTab2TL = (
    scenario?.operation_parameters?.threat_level ||
    scenario?.operation_parameters?.environmental_factors?.threat_level ||
    ''
  ).toUpperCase();
  const environment = TAB3_AD_TO_ENV[rawTab3AD]
                   || TAB2_THREAT_TO_ENV[rawTab2TL]
                   || 'PERMISSIVE';
  const envData = ENVIRONMENT_MODIFIERS[environment] || ENVIRONMENT_MODIFIERS.PERMISSIVE;

  // BUG-141: When Tab3 code present, read label + sortie_modifier from AD_THREAT_MODIFIERS
  // directly so Tab5 matches Tab3 exactly. Fall back to AD_LABELS/ENVIRONMENT_MODIFIERS
  // when only Tab2 data is available (Tab3 not configured yet).
  const tab3AdEntry = rawTab3AD ? (AD_THREAT_MODIFIERS?.[rawTab3AD] ?? null) : null;
  const adLabel    = tab3AdEntry ? tab3AdEntry.description : AD_LABELS[environment];
  const airModEnvAD = tab3AdEntry
    ? (tab3AdEntry.sortie_modifier ?? 1.0)
    : (envData.air_availability_modifier ?? 1);

  // ── ENV-002 FIX: Weather ─────────────────────────────────────────────────
  // Tab3 saves weather as WEATHER_MODIFIERS keys (VMC_CLEAR, IMC_LIGHT_RAIN etc).
  // Tab2 saves environmental_factors.weather as short keys (CLEAR, LIGHT_RAIN etc).
  // WEATHER_MODIFIERS may use either convention — try Tab3 key first, then Tab2 key.
  const WEATHER_LABELS = {
    VMC_CLEAR:       'VMC — Clear',         CLEAR:       'VMC — Clear',
    IMC_LIGHT_RAIN:  'Light Rain',           LIGHT_RAIN:  'Light Rain',
    IMC_HEAVY_RAIN:  'Heavy Rain',           HEAVY_RAIN:  'Heavy Rain',
    IMC_SNOW:        'Snow / IMC',           SNOW:        'Snow / IMC',
    IFR_FOG:         'Fog / IMC',            FOG:         'Fog / IMC',
    GROUNDED_STORM:  'Storm (grounded)',     STORM:       'Storm (grounded)'
  };
  const rawTab3Wx = (tecConfig?.weather || '').toUpperCase();
  const rawTab2Wx = (
    scenario?.operation_parameters?.environmental_factors?.weather || ''
  ).toUpperCase();
  const weatherKey = WEATHER_MODIFIERS[rawTab3Wx] ? rawTab3Wx
                   : WEATHER_MODIFIERS[rawTab2Wx] ? rawTab2Wx
                   : 'VMC_CLEAR';
  const wxData = WEATHER_MODIFIERS[weatherKey] || WEATHER_MODIFIERS.VMC_CLEAR
              || WEATHER_MODIFIERS.CLEAR  // fallback for older constants
              || { air: 1, ground: 1 };

  // ── Ground fleet ─────────────────────────────────────────────────────────
  // ENV-002 FIX: Read from tactical_evac_config.fleet, not ambulance_config.
  // Use available (operational) count, falling back to authorised count.
  let totalVehicles = 0;
  for (const val of Object.values(fleet)) {
    const cnt = typeof val === 'object' ? (val.available ?? val.count ?? 0) : (val ?? 0);
    totalVehicles += cnt;
  }
  const groundModAir  = envData.ground_speed_modifier ?? 1;
  const groundModWx   = wxData.ground ?? 1;
  const groundMod     = groundModAir * groundModWx;
  const availableVehicles = Math.round(totalVehicles * groundMod);
  const fleetUtilPct  = totalVehicles > 0
    ? Math.round((availableVehicles / totalVehicles) * 100)
    : 0;

  // ── Air pool ─────────────────────────────────────────────────────────────
  // ENV-002 FIX: Read from tactical_evac_config.air_pool (not medevac_config).
  // air_pool.nominal_daily_sorties is the Tab3-configured value already accounting
  // for pool size — no need to multiply by per-aircraft rate here.
  const nominalDailySorties = typeof airPool.nominal_daily_sorties === 'number'
    ? airPool.nominal_daily_sorties
    : 0;
  const poolLabel = airPool.pool_label
                 || (nominalDailySorties > 0 ? `${nominalDailySorties} sorties/day` : 'No air assets');
  // BUG-141: airModEnvAD already resolved above (AD_THREAT_MODIFIERS direct or ENVIRONMENT_MODIFIERS fallback)
  const airModWx  = wxData.air ?? 1;
  const airMod    = airModEnvAD * airModWx;
  const effectiveDailySorties = Math.round(nominalDailySorties * airMod);
  const degradationPct        = Math.round((1 - airMod) * 100);
  const combinedPct           = Math.round(airMod * 100);

  // ── Warnings ─────────────────────────────────────────────────────────────
  const warnings = [];
  if (degradationPct > 30)
    warnings.push(`Air availability degraded ${degradationPct}% (${environment} + ${weatherKey})`);
  if (fleetUtilPct < 50 && totalVehicles > 0)
    warnings.push(`Ground fleet utilisation low: ${fleetUtilPct}% (route/weather impact)`);
  if (environment === 'DENIED')
    warnings.push('DENIED airspace — MEDEVAC helicopter ops effectively suspended');

  return {
    ad_threat:         environment,
    ad_label:          adLabel,        // BUG-141: from AD_THREAT_MODIFIERS.description when Tab3 code present
    ad_modifier:       airModEnvAD,    // BUG-141: from AD_THREAT_MODIFIERS.sortie_modifier when Tab3 code present
    weather:           weatherKey,
    weather_label:     WEATHER_LABELS[weatherKey] || weatherKey,
    weather_modifier:  airModWx,
    combined_modifier: airMod,
    combined_pct:      combinedPct,
    fleet: {
      total_vehicles:     totalVehicles,
      available_vehicles: availableVehicles,
      utilization_pct:    fleetUtilPct
    },
    air: {
      nominal_daily_sorties:   nominalDailySorties,
      effective_daily_sorties: effectiveDailySorties,
      degradation_pct:         degradationPct,
      pool_label:              poolLabel
    },
    warnings
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  calculateInterFacilityDistance,
  calculateEffectiveAssets,
  calculateGroundCapacity,
  calculateAirCapacity,
  ENVIRONMENT_MODIFIERS,
  TIME_OF_DAY_MODIFIERS,
  WEATHER_MODIFIERS
};

const evacuationCapacityCalculator = {
  calculateFacilityCapacity,
  calculateEvacuationChainCapacity,
  calculateGoldenHourAchievement,
  prepareForCasualtyCalculator,
  getTacticalEvacSummary,
  calculateInterFacilityDistance,
  calculateEffectiveAssets,
  ENVIRONMENT_MODIFIERS
};

export default evacuationCapacityCalculator;
